import { Inbox, Lock, RefreshCw, TriangleAlert, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';

/**
 * The states every page must handle: nothing yet, something broke, and you may not
 * look at this.
 *
 * All three are written for a church administrator. "No records found" tells someone
 * what happened; "Add your first fund so you can start recording offerings" tells them
 * what to do, and the difference is whether they close the tab.
 */

interface BaseStateProps {
  icon?: ReactNode;
  title: string;
  /** One sentence. If it needs two, the feature needs a help article, not a longer state. */
  body?: string;
  action?: ReactNode;
  className?: string;
  /** Fills the available height, for a state that is the whole page. */
  full?: boolean;
}

const Shell = ({ icon, title, body, action, className, full }: BaseStateProps) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-3 px-6 text-center',
      full ? 'min-h-[24rem] py-16' : 'py-12',
      className,
    )}
  >
    {icon ? (
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-sunken text-fg-tertiary">
        {icon}
      </span>
    ) : null}
    <h2 className="text-card-title font-semibold text-fg">{title}</h2>
    {body ? <p className="max-w-sm text-body-sm text-fg-secondary">{body}</p> : null}
    {action ? <div className="mt-1">{action}</div> : null}
  </div>
);

export const EmptyState = ({
  icon = <Inbox className="h-6 w-6" />,
  ...props
}: BaseStateProps) => <Shell icon={icon} {...props} />;

export interface ErrorStateProps extends Omit<BaseStateProps, 'icon' | 'action'> {
  onRetry?: () => void;
  retryLabel?: string;
  /** Shown in small print so a church can quote it to support. */
  requestId?: string;
  offline?: boolean;
}

export const ErrorState = ({
  onRetry,
  retryLabel,
  requestId,
  offline = false,
  ...props
}: ErrorStateProps) => (
  <div>
    <Shell
      icon={
        offline ? <WifiOff className="h-6 w-6" /> : <TriangleAlert className="h-6 w-6" />
      }
      action={
        onRetry ? (
          <Button
            variant="outline"
            onClick={onRetry}
            leadingIcon={<RefreshCw className="h-4 w-4" />}
          >
            {retryLabel}
          </Button>
        ) : undefined
      }
      {...props}
    />
    {requestId ? (
      <p className="text-center font-numeric text-caption text-fg-tertiary">{requestId}</p>
    ) : null}
  </div>
);

/**
 * Never a dead end. It says who can grant access, so the user's next step is a
 * conversation rather than a support ticket.
 */
export const PermissionDeniedState = (props: Omit<BaseStateProps, 'icon'>) => (
  <Shell icon={<Lock className="h-6 w-6" />} {...props} />
);
